import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../../services/firebase';
import { Client, GeoPendenciaTipo, geoPendenciaMotivoLabel, geoPendenciaTipoLabel } from '../../types';
import ClientFormModal from '../../components/ClientFormModal';
import Pagination, { usePagination } from '../../components/Pagination';
import {
  ORDEM_TIPOS, contarPorTipo, filtrarPendencias, linhaEndereco, linhasParaExportar, opcoesDosFiltros, ordenarPendencias,
  pendenciaDe, ultimaVisitaCurta,
} from '../../utils/pendenciasEndereco';

// "Endereços a corrigir" — a fila do COMERCIAL. O servidor confere coordenada e endereço de cada cliente contra
// o CNEFE 2022 do IBGE e marca o que não fecha (`geoPendencia` + `geoPendente: true`). Aqui o comercial vê o que
// corrigir NO SANKHYA; a sincronização traz a correção e o cliente sai da lista sozinho — a página só LÊ.
// Acesso total (rota `access="full"` e item só no menu de acesso total): é a regra `isFullAccess()` de
// clients que permite a consulta ampla abaixo; o gestor com escopo teria a consulta inteira recusada.
// Regras de ordem/filtro/contagem em utils/pendenciasEndereco.ts (cobertas por jest).

// Tom de cada tipo: vermelho = a coordenada leva a lugar errado; âmbar = o endereço não resolve; cinza = cidade.
const TONS: Record<GeoPendenciaTipo, { fg: string; bg: string }> = {
  geoSuspeita: { fg: '#dc2626', bg: '#fee2e2' },
  geoNaoLocalizado: { fg: '#b45309', bg: '#fef3c7' },
  cidadeInvalida: { fg: '#6b7280', bg: '#f3f4f6' },
};
const TOM_DESCONHECIDO = TONS.cidadeInvalida;

export default function EnderecosPage() {
  const [clientes, setClientes] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [motivo, setMotivo] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [uf, setUf] = useState('');
  const [editing, setEditing] = useState<Client | null>(null);

  // AO VIVO (onSnapshot): o cliente some da lista assim que o servidor limpa a pendência, sem recarregar.
  // where('geoPendente','==',true) usa o índice de campo único (automático) — nada de índice composto; a ordem
  // é feita no navegador. `includeMetadataChanges` é o que separa "do cache" de "o servidor respondeu": o cache
  // persistente do painel (IndexedDB) pode responder VAZIO antes do servidor, e sem o aviso de quando o servidor
  // confirma, a tela diria "nenhum endereço a corrigir" (ou ficaria em "Carregando..." para sempre).
  useEffect(() => {
    const q = query(collection(db, 'clients'), where('geoPendente', '==', true));
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        setClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Client[]);
        setErro(null);
        if (!snap.metadata.fromCache || !snap.empty) setLoading(false);
        setAtualizando(snap.metadata.fromCache);
      },
      (e: any) => {
        console.warn('Endereços a corrigir: falha ao carregar', e);
        setErro(e?.code === 'permission-denied'
          ? 'Sem permissão para ler os clientes. Esta página é do acesso total.'
          : 'Não foi possível carregar os endereços a corrigir. Verifique a conexão.');
        setLoading(false);
        setAtualizando(false);
      }
    );
    return () => unsub();
  }, []);

  // A fila: só ATIVOS (como a lista de Clientes) e só quem tem pendência utilizável, na ordem da fila.
  const pendentes = useMemo(
    () => ordenarPendencias(clientes.filter((c) => c.active !== false && pendenciaDe(c))),
    [clientes]
  );
  const contagem = useMemo(() => contarPorTipo(pendentes), [pendentes]);
  const opcoes = useMemo(() => opcoesDosFiltros(pendentes), [pendentes]);
  const filtrados = useMemo(
    () => filtrarPendencias(pendentes, { busca, motivo, vendedor, uf }),
    [pendentes, busca, motivo, vendedor, uf]
  );

  const pager = usePagination(filtrados, `${busca}|${motivo}|${vendedor}|${uf}`);

  // Planilha .xlsx de verdade (mesma biblioteca `xlsx` dos importadores do painel), com o que está na tela:
  // filtros e ordem aplicados, todas as páginas. `writeFileXLSX`, e não `writeFile`: o `writeFile` arrasta os
  // escritores de TODOS os formatos (xls, ods, csv...) para o bundle principal — medido no build: +29 KB gzip
  // contra +11 KB só com o de .xlsx.
  const exportar = () => {
    const linhas = linhasParaExportar(filtrados);
    if (linhas.length === 0) {
      alert('Não há dados para exportar.');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(linhas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Endereços a corrigir');
    XLSX.writeFileXLSX(wb, `enderecos-a-corrigir-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const total = pendentes.length;

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>Endereços a corrigir</h1>
          <p style={styles.subtitle}>
            {loading ? '—' : total} {total === 1 ? 'cliente' : 'clientes'} · some sozinho quando o Sankhya é corrigido
            {!loading && atualizando && <span style={styles.refreshing}> · atualizando…</span>}
          </p>
        </div>
        <button style={styles.exportBtn} onClick={exportar} disabled={loading}>Exportar Excel</button>
      </div>

      <div style={styles.cards}>
        {ORDEM_TIPOS.map((t) => (
          <div key={t} style={{ ...styles.card, borderLeft: `4px solid ${TONS[t].fg}` }}>
            <div style={{ ...styles.cardValue, color: TONS[t].fg }}>{loading ? '—' : contagem[t]}</div>
            <div style={styles.cardLabel}>{geoPendenciaTipoLabel(t)}</div>
          </div>
        ))}
      </div>

      <div style={styles.filters}>
        <input
          style={styles.search}
          placeholder="Buscar por nome, código ou cidade..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select style={styles.select} value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          <option value="">Todos os motivos</option>
          {opcoes.motivos.map((g) => (
            <optgroup key={g.tipo} label={geoPendenciaTipoLabel(g.tipo)}>
              {g.motivos.map((m) => <option key={m} value={m}>{geoPendenciaMotivoLabel(m)}</option>)}
            </optgroup>
          ))}
        </select>
        <select style={styles.select} value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
          <option value="">Todos os vendedores</option>
          {opcoes.vendedores.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select style={styles.select} value={uf} onChange={(e) => setUf(e.target.value)}>
          <option value="">Todas as UFs</option>
          {opcoes.ufs.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>

      {erro && <p style={styles.erro}>{erro}</p>}

      {loading ? (
        <p>Carregando...</p>
      ) : !erro && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Código', 'Cliente', 'O que corrigir no Sankhya', 'Vendedor', 'Últ. visita'].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pager.pageItems.map((c) => {
                const p = pendenciaDe(c)!;
                const tom = TONS[p.tipo as GeoPendenciaTipo] || TOM_DESCONHECIDO;
                return (
                  <tr
                    key={c.id}
                    style={styles.tr}
                    tabIndex={0}
                    onClick={() => setEditing(c)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setEditing(c); }}
                  >
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{c.sankhya?.codparc ?? '—'}</td>
                    <td style={styles.td}>
                      <strong>{c.name || c.tradeName || '—'}</strong>
                      <div style={styles.sub}>{linhaEndereco(c.address)}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: tom.fg, background: tom.bg }}>{geoPendenciaTipoLabel(p.tipo)}</span>
                      {!!p.detalhe && <div style={styles.sub}>{p.detalhe}</div>}
                    </td>
                    <td style={styles.td}>{c.sellerName || '—'}</td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{ultimaVisitaCurta(c.lastCheckinAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtrados.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999', padding: 32 }}>
              {total === 0 ? 'Nenhum endereço a corrigir.' : 'Nenhum cliente encontrado.'}
            </p>
          )}
        </div>
      )}

      {!loading && !erro && <Pagination pager={pager} label="clientes" />}

      {/* A mesma ficha da ClientsPage: o admin edita; os demais papéis de acesso total abrem em somente leitura
          (quem decide é o próprio ClientFormModal). O onSnapshot acima reflete a edição sozinho. */}
      {editing && (
        <ClientFormModal
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  refreshing: { color: '#f5821f', fontWeight: 600 },
  exportBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 20 },
  card: { background: '#fff', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  cardValue: { fontSize: 28, fontWeight: 800 },
  cardLabel: { fontSize: 13, color: '#666', marginTop: 4 },
  filters: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 },
  search: { flex: 1, minWidth: 240, border: '1px solid #e0e0e0', borderRadius: 10, padding: '10px 14px', fontSize: 15, boxSizing: 'border-box', background: '#fff' },
  select: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, background: '#fff', cursor: 'pointer' },
  erro: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: 12, borderRadius: 8 },
  tableWrapper: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 760 },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #f0f0f0', background: '#fafafa', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  td: { padding: '12px 16px', fontSize: 14, color: '#333', verticalAlign: 'top' },
  sub: { marginTop: 4, fontSize: 12, color: '#666' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
};
